import { Rail } from "@circle-react-uikit/Rail";
import { RailBarPortal } from "@circle-react/components/shared/AiSummary/RailBarPortal";
import {
  FADE_OUT_DURATION,
  getAnimationDelay,
} from "@circle-react/components/Spaces/AiSummary/animations";
import { SpaceSummaryContent } from "@circle-react/components/Spaces/AiSummary/SummaryContent";
import { toLocaleString } from "@circle-react/helpers/number";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useCircleV4 } from "@circle-react/hooks/useCircleV4";
import type { Source } from "@circle-react/types/AiSummary/Source";
import { startOfTomorrow, subDays, subMonths } from "date-fns";
import { useEffect, useMemo, useState } from "react";

import { t } from "@/i18n-js/instance";
import { AiSummary } from "@/react/components/shared/AiSummary";
import { NoPostsState } from "@/react/components/Spaces/AiSummary/NoPostsState";
import { getShortDateRange } from "@/react/helpers/dateTimeHelpers/getLocalizedRange";
import {
  SPACE_DATE_RANGE,
  type SpaceDateRange,
} from "@/react/types/AiSummary/SpaceSummary";
import type { Topic } from "@/react/types/AiSummary/Topic";
import type { Space } from "@/react/types/Space";

import { useSpaceSummary } from "../../hooks/useSpaceSummary";
import { SpaceSummaryRailV4 } from "./SpaceSummaryRailV4";

type SpaceSummaryType = "chat" | "post";

export interface SpaceSummaryRailProps {
  dateRange: SpaceDateRange;
  firstUnreadMessageId?: string;
  isOpen: boolean;
  onClose: () => void;
  shouldHideRailbarDragger?: boolean;
  space: Space;
  type: SpaceSummaryType;
}

export const SpaceSummaryRail = (props: SpaceSummaryRailProps) => {
  const { isCircleV4 } = useCircleV4();
  if (isCircleV4) return <SpaceSummaryRailV4 {...props} />;
  return <SpaceSummaryRailV3 {...props} />;
};

const SpaceSummaryRailV3 = ({
  isOpen,
  onClose,
  space,
  dateRange,
  type,
  shouldHideRailbarDragger = true,
  firstUnreadMessageId,
}: SpaceSummaryRailProps) => {
  const dateFrom = useMemo(() => {
    if (dateRange === SPACE_DATE_RANGE.FROM_UNREAD) return undefined;
    const now = startOfTomorrow();
    return dateRange === SPACE_DATE_RANGE.LAST_7_DAYS
      ? subDays(now, 7).toISOString()
      : subMonths(now, 1).toISOString();
  }, [dateRange]);

  const {
    spaceSummary,
    isLoadingSpaceSummary,
    isNoDataError,
    contentCutOffMessage,
  } = useSpaceSummary({
    space,
    dateFrom: dateRange === SPACE_DATE_RANGE.FROM_UNREAD ? undefined : dateFrom,
    enabled: isOpen,
    type,
    firstUnreadMessageId:
      dateRange === SPACE_DATE_RANGE.FROM_UNREAD
        ? firstUnreadMessageId
        : undefined,
    dateRange,
  });

  const [isFadingOut, setIsFadingOut] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);
  const formattedDateRange = dateFrom
    ? getShortDateRange(new Date(dateFrom), new Date())
    : t("ai_spaces_summaries.from_unread");
  const sourceName = space.name;

  useEffect(() => {
    // Reset animation state when dateRange changes
    setAnimationKey(prev => prev + 1);
  }, [dateRange]);

  const handleCollapseClick = (): void => {
    setIsFadingOut(true);
    setTimeout(() => {
      setIsFadingOut(false);
      onClose();
    }, FADE_OUT_DURATION);
  };

  const selectorQuery = `#rail-bar-header`;
  const [portalElement, setPortalElement] = useState<Element | null>(null);

  useEffect(() => {
    setPortalElement(document.querySelector(selectorQuery));
  }, [selectorQuery]);

  if (!portalElement) {
    return null;
  }

  const { topics = [] } = spaceSummary || {};
  const allSources =
    topics
      .flatMap((topic: Topic) => topic.relevant_posts)
      .filter((source: unknown): source is Source => source !== undefined) ??
    [];

  const totalRecords = spaceSummary?.total_records;

  return (
    <RailBarPortal
      shouldShowRailBar={isOpen}
      shouldHideRailbarDragger={shouldHideRailbarDragger}
    >
      <Rail.Content heightGap={16}>
        <div
          className={classNames("transition-opacity duration-300", {
            "opacity-0": isFadingOut,
            "opacity-100": !isFadingOut,
          })}
        >
          <div className="bg-ai-summary absolute inset-0 bg-cover bg-center bg-no-repeat" />
          {isNoDataError && !isLoadingSpaceSummary ? (
            <NoPostsState onCollapseClick={handleCollapseClick} />
          ) : (
            <div className="relative" key={animationKey}>
              <AiSummary onCollapseClick={handleCollapseClick}>
                <SpaceSummaryContent
                  spaceSummary={spaceSummary}
                  sourceName={sourceName}
                  formattedDateRange={formattedDateRange}
                  isLoadingSpaceSummary={isLoadingSpaceSummary}
                  allSources={allSources}
                  topics={topics}
                  contentCutOffMessage={contentCutOffMessage}
                />
                {typeof totalRecords === "number" && totalRecords > 0 && (
                  <AiSummary.Footer>
                    <div
                      className="animate-fade-in-slow opacity-0"
                      style={{
                        animationDelay: getAnimationDelay(5, topics.length),
                        animationFillMode: "forwards",
                      }}
                    >
                      <AiSummary.FooterNote>
                        {t(
                          `ai_spaces_summaries.${type === "chat" ? "messages" : "posts"}_summarized`,
                          {
                            count: totalRecords,
                            formatted_count: toLocaleString(totalRecords),
                          },
                        )}
                      </AiSummary.FooterNote>
                    </div>
                  </AiSummary.Footer>
                )}
              </AiSummary>
            </div>
          )}
        </div>
      </Rail.Content>
    </RailBarPortal>
  );
};
